'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetRewardsStreakRow } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { TierEmblem } from '@/components/streaks/tier-emblem';
import { AnimatedButton } from '@/components/ui/animated-button';
import { FireIcon } from '@/components/ui/icon/glyphs';
import { StatusPill } from '@/components/ui/status-pill';
import { NetworkLink } from '@/i18n/network-link';
import { daysToNextTier, levelForDays, nextTier, type TierSlug, tierForLevel } from '@/lib/streaks';
import { cn } from '@/lib/ui/utils';
import { EarnCard } from './earn-card';
import { useRewardsTips } from './info-tip';
import { TRADE_HREF } from './trade-link';
interface TradingStreakPanelProps {
  streak: GetRewardsStreakRow | undefined;
  onClaim: () => void;
  claiming?: boolean;
}

/** Mon→Sun labels; the server's `week` strip is always ordered Monday first. */
const DAY_LABELS = [{
  key: 'monday',
  label: 'M'
}, {
  key: 'tuesday',
  label: 'T'
}, {
  key: 'wednesday',
  label: 'W'
}, {
  key: 'thursday',
  label: 'T'
}, {
  key: 'friday',
  label: 'F'
}, {
  key: 'saturday',
  label: 'S'
}, {
  key: 'sunday',
  label: 'S'
}] as const;

/**
 * Rewards card for the trading streak. The streak, today's day boundary, and the
 * week strip all come from the server. Before a trade today the action is
 * "Trade" (into the markets feed); once traded it becomes "Claim". The server
 * still rejects a claim without a qualifying trade, so stale card state can't
 * pay out.
 */
export function TradingStreakPanel(t0) {
  const $ = _c(34);
  const {
    streak,
    onClaim,
    claiming
  } = t0;
  const t = useTranslations("rewards");
  const tips = useRewardsTips();
  const current = streak?.current ?? 0;
  let t1;
  if ($[0] !== current) {
    t1 = levelForDays(current);
    $[0] = current;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const level = t1;
  const description = useStreakSubtitle(current, level);
  let t2;
  if ($[2] !== streak?.week) {
    t2 = streak?.week ?? [];
    $[2] = streak?.week;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const week = t2;
  let t3;
  if ($[4] !== t) {
    t3 = t("streak.title");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = tips.streak;
  let t5;
  if ($[6] !== t) {
    t5 = t("streak.newTag");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <StatusPill tone="success" size="xs">{t5}</StatusPill>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("streak.pill");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = Math.min(current, 7) / 7 * 100;
  const t9 = `${Math.min(current, 7)}/7`;
  let t10;
  if ($[12] !== level || $[13] !== streak?.today || $[14] !== week) {
    t10 = DAY_LABELS.map((day, i) => {
      const cell = week[i];
      const isToday = cell !== undefined && cell.date === streak?.today;
      return <span key={day.key} data-traded={cell?.traded === true} className={cn("flex h-8 flex-1 items-center justify-center rounded-panel-inset border text-caption", cell?.traded === true ? "border-feedback-success-border bg-feedback-success-surface text-feedback-success-content" : "text-foreground-tertiary")}>{isToday ? <TierEmblem level={level} size={20} /> : day.label}</span>;
    });
    $[12] = level;
    $[13] = streak?.today;
    $[14] = week;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== claiming || $[17] !== onClaim || $[18] !== streak?.claimedToday || $[19] !== streak?.tradedToday || $[20] !== t) {
    t11 = <div className="ml-1 flex shrink-0">{claiming ? <AnimatedButton variant="secondary" data-testid="claim-streak" disabled={true} loading={true} aria-label={t("streak.claiming")} /> : streak?.claimedToday === true ? <AnimatedButton data-testid="claim-streak" variant="secondary" disabled={true}>{t("streak.claimed")}</AnimatedButton> : streak?.tradedToday === true ? <AnimatedButton variant="secondary" data-testid="claim-streak" onClick={onClaim}>{t("streak.claim")}</AnimatedButton> : <AnimatedButton data-testid="claim-streak" asChild={true}><NetworkLink href={TRADE_HREF} target="_blank" rel="noopener noreferrer">{t("streak.trade")}</NetworkLink></AnimatedButton>}</div>;
    $[16] = claiming;
    $[17] = onClaim;
    $[18] = streak?.claimedToday;
    $[19] = streak?.tradedToday;
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className="flex items-center gap-1">{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== description || $[26] !== t12 || $[27] !== t3 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9 || $[32] !== tips.streak) {
    t13 = <EarnCard data-testid="streak-panel" title={t3} tip={t4} titleAdornment={t6} points={t7} pointsIcon={FireIcon} description={description} progress={t8} progressLabel={t9}>{t12}</EarnCard>;
    $[25] = description;
    $[26] = t12;
    $[27] = t3;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = tips.streak;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function useStreakSubtitle(current, level) {
  const $ = _c(5);
  const t = useTranslations("rewards");
  let t0;
  let t1;
  if ($[0] !== current || $[1] !== level || $[2] !== t) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const tier = tierForLevel(level);
      if (current === 0 || tier === null) {
        t1 = t("streak.subtitleStart");
        break bb0;
      }
      const next = nextTier(level);
      const toNext = daysToNextTier(current, level);
      if (next === null || toNext === null) {
        t1 = t("streak.subtitleMax", {
          tier: tierLabel(t, tier.slug)
        });
        break bb0;
      }
      t0 = t("streak.subtitleWithNext", {
        tier: tierLabel(t, tier.slug),
        toNext,
        nextTier: tierLabel(t, next.slug)
      });
    }
    $[0] = current;
    $[1] = level;
    $[2] = t;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  return t0;
}

/** Localised tier name for a trading-streak tier slug. */
function tierLabel(t: ReturnType<typeof useTranslations<'rewards'>>, slug: TierSlug): string {
  switch (slug) {
    case 'spark':
      return t('streak.tierSpark');
    case 'ember':
      return t('streak.tierEmber');
    case 'flame':
      return t('streak.tierFlame');
    case 'blaze':
      return t('streak.tierBlaze');
    case 'wildfire':
      return t('streak.tierWildfire');
    case 'inferno':
      return t('streak.tierInferno');
    case 'eternal':
      return t('streak.tierEternal');
  }
}